import * as React from "react";
import { AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

export const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

export function validateEmail(value: string, required = false): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return required ? "Email is required" : null;
  }
  if (!EMAIL_REGEX.test(trimmed)) {
    return "Please enter a valid email address (e.g. name@example.com)";
  }
  return null;
}

export function validateNumber(
  value: string | number,
  options?: {
    required?: boolean;
    min?: number | string;
    max?: number | string;
    step?: number | string;
  }
): string | null {
  const str = String(value ?? "").trim();
  if (!str) {
    return options?.required ? "This field is required" : null;
  }
  const num = Number(str);
  if (Number.isNaN(num)) {
    return "Please enter a valid number";
  }
  if (options?.min !== undefined && options?.min !== "" && num < Number(options.min)) {
    return `Value must be at least ${options.min}`;
  }
  if (options?.max !== undefined && options?.max !== "" && num > Number(options.max)) {
    return `Value must not exceed ${options.max}`;
  }
  if (options?.step !== undefined && options?.step !== "any" && options?.step !== "") {
    const stepNum = Number(options.step);
    if (!Number.isNaN(stepNum) && stepNum > 0) {
      const minVal = options.min !== undefined && options.min !== "" ? Number(options.min) : 0;
      const remainder = Math.abs((num - minVal) % stepNum);
      if (remainder > 0.0001 && Math.abs(remainder - stepNum) > 0.0001) {
        return stepNum === 1 ? "Please enter a whole number" : `Value must be in increments of ${stepNum}`;
      }
    }
  }
  return null;
}

export interface InputProps extends React.ComponentProps<"input"> {
  /** Custom error message or boolean indicating error state */
  error?: string | boolean;
  /** Explicit error message (overrides auto-generated message) */
  errorMessage?: string;
  /** Whether to automatically validate email and number types (default: true) */
  autoValidate?: boolean;
  /** Whether to display the inline error message below the input (default: true) */
  showErrorMessage?: boolean;
  /** Custom validator function returning error string or null */
  validate?: (value: string) => string | null | undefined;
  /** Optional container class name for the wrapper */
  containerClassName?: string;
  /** Optional class name for the error message element */
  errorClassName?: string;
  /** Allow exponential notation (e.g. 1e5) for number inputs (default: false) */
  allowExponential?: boolean;
  /** Bypass wrapper container and render naked input */
  noWrapper?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      containerClassName,
      errorClassName,
      type,
      value,
      defaultValue,
      onChange,
      onBlur,
      onKeyDown,
      error,
      errorMessage: customErrorMessage,
      autoValidate = true,
      showErrorMessage = true,
      validate,
      allowExponential = false,
      noWrapper = false,
      required,
      min,
      max,
      step,
      id,
      ...props
    },
    ref
  ) => {
    const innerRef = React.useRef<HTMLInputElement | null>(null);
    const [touched, setTouched] = React.useState(false);
    const [internalError, setInternalError] = React.useState<string | null>(null);

    // Merge external ref with internal ref
    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        innerRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
        }
      },
      [ref]
    );

    // Perform validation for the given string value
    const runValidation = React.useCallback(
      (val: string): string | null => {
        if (validate) {
          const res = validate(val);
          if (res) return res;
        }
        if (!autoValidate) return null;

        if (type === "email") {
          return validateEmail(val, Boolean(required));
        }

        if (type === "number") {
          return validateNumber(val, { required: Boolean(required), min, max, step });
        }

        if (required && !val.trim()) {
          return "This field is required";
        }

        return null;
      },
      [autoValidate, type, required, min, max, step, validate]
    );

    // Re-validate when controlled value or constraints change if already touched
    React.useEffect(() => {
      if (touched) {
        const currentVal =
          value !== undefined ? String(value) : (innerRef.current?.value ?? "");
        setInternalError(runValidation(currentVal));
      }
    }, [value, touched, runValidation]);

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setTouched(true);
      const err = runValidation(e.target.value);
      setInternalError(err);
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (touched) {
        const err = runValidation(e.target.value);
        setInternalError(err);
      }
      onChange?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (type === "number" && !allowExponential && (e.key === "e" || e.key === "E")) {
        e.preventDefault();
      }
      if (
        type === "number" &&
        min !== undefined &&
        Number(min) >= 0 &&
        e.key === "-"
      ) {
        e.preventDefault();
      }
      onKeyDown?.(e);
    };

    const activeError =
      customErrorMessage ||
      (typeof error === "string" ? error : internalError);
    const isInvalid = Boolean(error || internalError);

    const generatedId = React.useId();
    const inputId = id || generatedId;
    const errorId = `${inputId}-error`;

    const inputElement = (
      <input
        ref={setRefs}
        id={inputId}
        type={type}
        data-slot="input"
        aria-invalid={isInvalid ? "true" : props["aria-invalid"]}
        aria-describedby={
          isInvalid && activeError && showErrorMessage
            ? errorId
            : props["aria-describedby"]
        }
        className={cn(
          "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input h-8 sm:h-9 w-full min-w-0 rounded-lg border bg-card px-3 py-1 text-[13px] transition-[color,box-shadow,border-color] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
          "focus-visible:border-ring focus-visible:ring-ring/30 focus-visible:ring-2",
          "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
          isInvalid &&
            "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20 text-destructive",
          className
        )}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        required={required}
        min={min}
        max={max}
        step={step}
        {...props}
      />
    );

    if (noWrapper) {
      return inputElement;
    }

    const isConstrained =
      className && /(^|\s)(w-\[|w-\d|max-w-)/.test(className);
    const isFlex1 = className && /(^|\s)flex-1/.test(className);

    return (
      <div
        className={cn(
          "relative flex flex-col",
          isFlex1 ? "flex-1" : isConstrained ? "w-fit" : "w-full",
          containerClassName
        )}
      >
        {inputElement}
        {showErrorMessage && isInvalid && activeError && (
          <p
            id={errorId}
            role="alert"
            className={cn(
              "text-[11px] text-destructive flex items-center gap-1 font-medium mt-1 animate-in fade-in-50 duration-150",
              errorClassName
            )}
          >
            <AlertCircle className="size-3 shrink-0" />
            <span>{activeError}</span>
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export { Input };
